import { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import { ArrowUp, Loader2 } from "lucide-react";
import { DataInsightsDialog } from "./DataInsightsDialog";
import { CustomViewCreatorDialog } from "./CustomViewCreatorDialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

// Cycling placeholder texts
const PLACEHOLDERS = [
  "Ask AI about your data...",
  "Create a personal widget...",
];
const CYCLE_INTERVAL = 4000; // ms

interface DataInsightsInputProps {
  userId: string;
  isPremium: boolean | undefined;
  userCredits?: number;
}

export const DataInsightsInput = ({ userId, isPremium, userCredits }: DataInsightsInputProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [text, setText] = useState("");
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [dataInsightsOpen, setDataInsightsOpen] = useState(false);
  const [customViewOpen, setCustomViewOpen] = useState(false);
  const [initialQuestion, setInitialQuestion] = useState("");
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea downward
  const autoResize = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  // Cycling placeholder
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  useEffect(() => {
    // Only cycle if user is premium (they can create views)
    if (!isPremium) return;
    const timer = setInterval(() => {
      setPlaceholderIndex((prev) => (prev + 1) % PLACEHOLDERS.length);
    }, CYCLE_INTERVAL);
    return () => clearInterval(timer);
  }, [isPremium]);

  const currentPlaceholder = isPremium
    ? PLACEHOLDERS[placeholderIndex]
    : t('data_insights.input_placeholder', 'Ask AI about your data...');

  // Show overlay when input is focused
  const showOverlay = isMobile && isInputFocused;

  // Dispatch event to blur other page content
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('data-insights-input-overlay', {
      detail: { active: showOverlay }
    }));
  }, [showOverlay]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!text.trim()) return;

    // Check minimum credits (data insights costs 5 = 0.5 display)
    const insightsCreditCost = 5;
    if (!userCredits || userCredits < insightsCreditCost) {
      if (isPremium) {
        toast.error(t('notifications.toast.out_of_credits', "You're out of credits"));
        navigate("/credits-success");
      } else {
        toast.error("You're out of credits. Upgrade for unlimited AI.");
        navigate("/upgrade", { state: { fromCredits: true } });
      }
      return;
    }

    // Always open data insights — the AI will decide if it should redirect
    setInitialQuestion(text.trim());
    setDataInsightsOpen(true);
    setText("");
    if (inputRef.current) inputRef.current.style.height = 'auto';
    setIsInputFocused(false);
    inputRef.current?.blur();
  };

  // Called when the data-insights AI defers to the view creator
  const handleRedirectToViewCreator = (prompt: string) => {
    if (!isPremium) {
      toast.error(t('custom_views.pro_required_upgrade', 'Custom widgets are a Pro feature. Upgrade to unlock.'));
      navigate("/upgrade", { state: { fromCredits: true } });
      return;
    }
    const viewCreditCost = 30;
    if (!userCredits || userCredits < viewCreditCost) {
      toast.error(t('notifications.toast.out_of_credits', "You're out of credits"));
      navigate("/credits-success");
      return;
    }
    setInitialQuestion(prompt);
    setCustomViewOpen(true);
  };

  return (
    <>
      {/* Background blur overlay for mobile */}
      {showOverlay && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 animate-in fade-in duration-200"
          onClick={() => {
            setIsInputFocused(false);
            inputRef.current?.blur();
          }}
        />
      )}

      <form 
        onSubmit={handleSubmit} 
        className={`relative w-full ${showOverlay ? 'z-50' : ''}`}
      >
        <div className="relative w-full">
          <textarea
            ref={inputRef}
            value={text}
            onChange={(e) => {
              setText(e.target.value.slice(0, 200));
              autoResize();
            }}
            onFocus={() => setIsInputFocused(true)}
            onBlur={() => {
              // Small delay to allow button click to register
              setTimeout(() => setIsInputFocused(false), 150);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit(e as unknown as React.FormEvent);
              }
            }}
            placeholder={currentPlaceholder}
            maxLength={200}
            rows={1}
            className={`flex w-full rounded-2xl border bg-background px-3 py-2 text-sm pr-12 transition-all duration-200 resize-none overflow-hidden ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-500/50 focus-visible:ring-offset-0 focus-visible:border-blue-500/50 disabled:cursor-not-allowed disabled:opacity-50 ${
              isMobile && showOverlay 
                ? 'bg-card/95 border-blue-500/50 shadow-lg' 
                : 'border-border'
            }`}
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2">
            <Button
              type="submit"
              size="icon"
              disabled={!text.trim() || isPremium === undefined}
              onMouseDown={(e) => e.preventDefault()}
              className={`h-8 w-8 rounded-full transition-all duration-200 ${
                text.trim() 
                  ? 'bg-blue-500 hover:bg-blue-600 text-white' 
                  : 'bg-muted text-muted-foreground'
              }`}
            >
              <ArrowUp className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </form>

      {/* Data Insights Dialog */}
      <DataInsightsDialog
        open={dataInsightsOpen}
        onOpenChange={(open) => {
          setDataInsightsOpen(open);
          if (!open) {
            setInitialQuestion("");
          }
        }}
        userId={userId}
        initialQuestion={initialQuestion}
        userCredits={userCredits}
        isPremium={isPremium}
        onRedirectToViewCreator={handleRedirectToViewCreator}
      />

      {/* Custom View Creator Dialog (pro only) */}
      <CustomViewCreatorDialog
        open={customViewOpen}
        onOpenChange={(open) => {
          setCustomViewOpen(open);
          if (!open) {
            setInitialQuestion("");
          }
        }}
        userId={userId}
        initialPrompt={initialQuestion}
        userCredits={userCredits}
        isPremium={isPremium}
      />
    </>
  );
};
